import React, { useEffect, useState } from "react";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "./ui/sheet";
import { api, formatApiErrorDetail, formatMXN } from "../lib/api";
import { toast } from "sonner";
import { Check, Clock, AlertCircle } from "lucide-react";

const STATUS_PILL = {
  overdue:  { bg: "bg-clay/15 text-clay", icon: AlertCircle },
  today:    { bg: "bg-sage-100 text-sage-900", icon: Clock },
  upcoming: { bg: "bg-bone text-soft", icon: Clock },
};

export default function PayRecurrenceSheet({ item, onClose, onPaid }) {
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (item) {
      setAmount(String(item.amount));
      setNote("");
    }
  }, [item]);

  if (!item) return null;

  const StatusIcon = STATUS_PILL[item.status]?.icon || Clock;
  const dueDt = new Date(item.due_date);
  const dueLabel = dueDt.toLocaleDateString("es-MX", { day: "numeric", month: "long" });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const amt = Number(amount);
    if (!amt || amt <= 0) {
      toast.error("El monto debe ser mayor a 0");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/recurrences/${item.recurrence_id}/pay`, {
        due_date: item.due_date,
        amount: amt,
        note: note || null,
      });
      toast.success(`${item.name} marcado como pagado`);
      window.dispatchEvent(new CustomEvent("fp:tx-created"));
      onPaid?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo registrar el pago");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Sheet open onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <SheetContent
        side="bottom"
        data-testid="pay-recurrence-sheet"
        className="bg-bone border-line rounded-t-3xl p-0 max-w-md mx-auto overflow-y-auto overscroll-contain"
        style={{ maxHeight: "92dvh", paddingBottom: "calc(env(safe-area-inset-bottom,0px) + 1.5rem)" }}
      >
        <div className="mx-auto mt-3 mb-1 h-1.5 w-12 rounded-full bg-line/80" aria-hidden />
        <SheetHeader className="text-left px-6 pt-2">
          <SheetTitle className="font-display text-2xl text-ink">Confirmar pago</SheetTitle>
          <SheetDescription className="text-soft text-sm">
            Se registrará como un movimiento real y se descontará del saldo de la cuenta.
          </SheetDescription>
        </SheetHeader>

        <div className="px-6 mt-3 mb-3">
          <div className="bg-white border border-line rounded-2xl px-4 py-3.5">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-sage-50 text-sage-700 flex items-center justify-center">
                <StatusIcon size={16} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-ink truncate">{item.name}</p>
                <p className="text-[11px] text-soft">
                  Vence {dueLabel} · {item.frequency}
                </p>
              </div>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 px-6">
          <div>
            <label className="text-[10px] font-bold uppercase tracking-[0.16em] text-sage-700 block mb-1">
              Monto a pagar
            </label>
            <input
              data-testid="pay-amount"
              type="number"
              step="0.01"
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="w-full bg-white border border-line rounded-xl px-3 py-3 outline-none focus:border-sage-500 text-ink text-2xl font-bold font-display tabular"
            />
            <p className="text-xs text-soft mt-1">
              {formatMXN(Number(amount) || 0)} ·{" "}
              {Number(amount) !== item.amount
                ? <span className="text-clay">Monto distinto al programado ({formatMXN(item.amount)})</span>
                : <span>Coincide con lo programado</span>}
            </p>
          </div>

          <div>
            <label className="text-[10px] font-bold uppercase tracking-[0.16em] text-sage-700 block mb-1">
              Nota (opcional)
            </label>
            <input
              data-testid="pay-note"
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Detalles del pago"
              className="w-full bg-white border border-line rounded-xl px-3 py-3 outline-none focus:border-sage-500"
            />
          </div>

          <button
            type="submit"
            data-testid="pay-confirm"
            disabled={submitting}
            className="w-full bg-sage-900 text-bone rounded-2xl py-3 font-bold hover:bg-sage-900/90 disabled:opacity-60 inline-flex items-center justify-center gap-2"
          >
            <Check size={14} strokeWidth={2.8} />
            {submitting ? "Registrando…" : "Confirmar pago"}
          </button>
          <p className="text-[11px] text-soft text-center leading-relaxed pb-2">
            Se creará un movimiento de {item.type === "gasto" ? "gasto" : "ingreso"} en la cuenta vinculada y la próxima fecha avanzará automáticamente.
          </p>
        </form>
      </SheetContent>
    </Sheet>
  );
}
